/**
 * Surveillance target DOM markers — mockup's `drawTarget()` ported to Mapbox.
 *
 * Layout: the rotated plane SVG is the only in-flow child of the marker
 * container. Optional label card / proximity tooltip / ack button are all
 * `position:absolute`, so the marker's bounding box stays glued to the
 * SVG. anchor:'center' then places the lat/lng point on the SVG's center,
 * matching the mockup's L.divIcon iconAnchor centering.
 *
 * Markers update in place: only innerHTML mutates when the visual
 * fingerprint changes; setLngLat handles position. The mapboxgl.Marker
 * itself is never destroyed except when the target leaves the feed.
 */
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useTargetsStore } from '@/lib/store/targets';
import { useSurvStore } from '@/lib/store/surv';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { mapAnimation } from '@/lib/map/instance';
import type { Target } from '@/lib/mqtt/schemas/targets';
import { altitudeToColor } from '@/lib/aircraft/altitudeColor';
import { renderAircraftMarker } from '@/lib/aircraft/tar1090Adapter';
import { droneContextFlags } from '@/lib/droneContextFlags';
import { formatKnots } from '@/lib/format';
import { escapeHtml } from '@/lib/map/markers/droneIcon';
import { RF_COLOR, multirotorSvg } from '@/lib/map/markers/rfIcons';
import { visibleTargets } from '@/lib/map/targetVisibility';

// Render-scale constants mirror lib/map/markers/aircraftMarkers.ts so a
// single user-facing size step (View menu → Aircraft icon size) produces
// the same physical icon size whether the aircraft came from MQTT
// (this layer) or anywhere else.
const BASE_RENDER_SCALE = 1.0;
const ICON_STEP = 0.12;
const SELECTED_SCALE_MULT = 1.18;
const STROKE_WIDTH = 0.4;
const STROKE_WIDTH_SELECTED = 0.5;

function scaleForStep(step: number, selected: boolean): number {
  const clamped = Math.max(-3, Math.min(3, step));
  const base = BASE_RENDER_SCALE * (1 + clamped * ICON_STEP);
  return selected ? base * SELECTED_SCALE_MULT : base;
}

// Sticky-hover state (ADSB-Exchange-style):
//   - mouseenter on a marker sets `activeHoverEl` and nulls `leftZone*` so
//     cursor motion *inside* the plane bbox never triggers dismissal.
//   - mouseleave records the exit coordinates in `leftZone*`.
//   - A single document-level mousemove listener (installed by
//     subscribeTargetMarkers) closes the tooltip only after the cursor has
//     actually moved >HOVER_DISMISS_PX from the exit point. If the plane
//     drifts away while the cursor stays put, the tooltip persists — which
//     is the whole point of the sticky behaviour.
const HOVER_DISMISS_PX = 4;
let activeHoverEl: HTMLElement | null = null;
let leftZoneX: number | null = null;
let leftZoneY: number | null = null;

// Aircraft icon colour is altitude-driven across every context (drones,
// surv-fused, surv-diagnostic). The earlier surv-ctx tint overrides
// (mockup `_drawFusedSurvTargets` + `_drawServiceSurvTargets`) hid the
// altitude gradient for no operational gain — the pill row already
// conveys which source is selected and the diagnostic filter restricts
// the set, so adding a colour layer was redundant.

function fmtFt(ft: number | null): string {
  if (ft == null) return '—';
  return ft >= 1000 ? `${(Math.round(ft / 100) / 10).toFixed(1)}k ft` : `${Math.round(ft)} ft`;
}

/** Callout identity for ADS-style targets: registration (falling back to the
 *  callsign) and make/model text; `--` while the feed omits them. */
export function targetIdentity(t: Pick<Target, 'callsign' | 'registration' | 'description'>) {
  return {
    id: t.registration ?? t.callsign,
    makeModel: t.description ?? '--',
  };
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function buildInnerHtml(
  t: Target,
  showLabels: boolean,
  isAcked: boolean,
  selectedDroneLatLng: { lat: number; lng: number; agl: number | null } | null,
  sizeStep: number,
  /** Whether this is the user-selected target — boosts scale + saturates
   *  the altitude colour, mirroring tar1090's selection convention. */
  selected: boolean,
  /** Suppress the alert tooltip + ack button — used in surveillance context
   * where the mockup `_drawSurvTarget` shows a lightweight hover tooltip,
   * not the persistent proximity overlay from drones ctx. */
  suppressAlertOverlay = false,
): { html: string; boxSize: number } {
  // Fill cascade — alert/acknowledged paint over everything in drones-ctx
  // (those carry operational meaning); otherwise the marker takes the
  // tar1090 altitude-HSL gradient regardless of context.
  let fill: string;
  if (t.proximity === 'alert' && !suppressAlertOverlay) {
    fill = '#c41a1a';
  } else if (t.proximity === 'acknowledged' && !suppressAlertOverlay) {
    fill = '#6b7280';
  } else {
    fill = altitudeToColor(t.altitude_ft, { selected });
  }
  // RF-detected drones are always multirotors (ENG-253) — draw the dedicated
  // multirotor instead of an aircraft silhouette.
  const rendered = t.rf
    ? {
        ...multirotorSvg(
          Math.round(34 * scaleForStep(sizeStep, selected)),
          // alert / acknowledged keep their operational colours, as for aircraft
          t.proximity === 'alert' && !suppressAlertOverlay
            ? '#c41a1a'
            : t.proximity === 'acknowledged' && !suppressAlertOverlay
              ? '#6b7280'
              : RF_COLOR,
          selected,
        ),
        noRotate: true,
      }
    : renderAircraftMarker({
    category: t.category,
    type: t.aircraft_type,
    altitudeFt: t.altitude_ft,
    fill,
    stroke: selected ? '#000000' : '#1f2937',
    strokeWidth: selected ? STROKE_WIDTH_SELECTED : STROKE_WIDTH,
    scale: scaleForStep(sizeStep, selected),
  });
  // The wrap is a fixed square big enough to contain the SVG at ANY heading
  // (worst case: the SVG diagonal). Keeps the marker's axis-aligned bbox
  // constant regardless of heading so Mapbox's anchor:'center' math never
  // drifts during rotation or zoom.
  const boxSize = Math.ceil(Math.max(rendered.width, rendered.height) * Math.SQRT2);
  // In surveillance context (`suppressAlertOverlay`) the mockup does NOT show
  // the proximity tooltip / ack stack — those belong to drones context.
  const isAlert = t.proximity === 'alert' && !suppressAlertOverlay;
  const relCol = t.proximity === 'alert' ? '#c41a1a' : (t.proximity === 'caution' ? '#b45309' : '#1a7a40');
  const lbBorder = isAlert ? 'rgba(196,26,26,.4)' : (t.trust === 'low' ? 'rgba(180,83,9,.3)' : 'rgba(100,120,200,.25)');

  let vertFt = t.vert_sep_ft;
  let horizStr: string;
  if (t.horiz_sep_nm != null) {
    horizStr = fmtFt(t.horiz_sep_nm * 6076.115);
  } else if (selectedDroneLatLng) {
    horizStr = fmtFt(haversineM(selectedDroneLatLng.lat, selectedDroneLatLng.lng, t.lat, t.lng) * 3.28084);
  } else {
    horizStr = '—';
  }
  if (vertFt == null && selectedDroneLatLng?.agl != null) {
    vertFt = t.altitude_ft - selectedDroneLatLng.agl;
  }
  const vertStr = vertFt == null ? '—' : `${vertFt >= 0 ? '+' : ''}${Math.round(vertFt)} ft`;

  // The plane sits absolutely centered inside a fixed-size box (`boxSize`).
  // Rotation happens on the inner div so the outer wrap's bbox is constant.
  // tar1090 silhouettes are nose-up (north-up) — rotate by `track` directly.
  // `rendered.noRotate` covers shapes that aren't directional (ground
  // vehicles, helicopters drawn from above, etc.).
  const rotateDeg = rendered.noRotate ? 0 : t.heading;
  const planeWrapped =
    `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(${rotateDeg.toFixed(1)}deg);transform-origin:center">` +
    rendered.svg +
    `</div>` +
    (isAlert && isAcked
      ? `<div style="position:absolute;top:-8px;right:-8px;width:16px;height:16px;border-radius:50%;background:#c41a1a;border:2px solid white;display:flex;align-items:center;justify-content:center;font-size:0.692em;color:white;font-weight:700;z-index:10;pointer-events:none">!</div>`
      : '');

  // RF-detected drones (ENG-253): the sensor's own strings, so escape them.
  // Missing values render `--`.
  const rf = t.rf;
  const rfLabelHtml = rf
    ? `<div style="color:#444">ID: ${escapeHtml(rf.serial)}</div>` +
      `<div style="color:#444">Freq: ${rf.freq_mhz != null ? `${rf.freq_mhz} MHz` : '--'}</div>` +
      `<div style="color:#444">RSSI: ${rf.rssi_dbm != null ? `${rf.rssi_dbm} dBm` : '--'}</div>`
    : '';
  // Non-RF targets: registration (falling back to callsign) as the header and
  // the feed's `desc` as make/model. Both come straight from `r` / `desc`, so
  // they fill in as soon as the backend sends them; until then `--`.
  const ident = targetIdentity(t);
  // ENG-271: the Make/Model line is hidden for now (`calloutMakeModel`).
  const makeModelHtml = rf || !droneContextFlags.calloutMakeModel
    ? ''
    : `<div style="color:#444">Make/Model: ${escapeHtml(ident.makeModel)}</div>`;
  const labelHtml = showLabels
    ? `<div style="position:absolute;left:calc(100% + 9px);top:50%;transform:translateY(-50%);` +
      `background:rgba(255,255,255,.96);border:1px solid ${lbBorder};border-radius:6px;` +
      `padding:5px 9px;font-family:IBM Plex Mono,monospace;font-size:0.769em;line-height:1.65;` +
      `color:#1a1a1a;box-shadow:0 2px 8px rgba(0,0,0,.13);white-space:nowrap;pointer-events:none">` +
      `<div style="font-family:Inter,sans-serif;font-size:0.846em;font-weight:700;margin-bottom:2px;color:#111">${rf ? escapeHtml(rf.model ?? '--') : escapeHtml(ident.id)}</div>` +
      rfLabelHtml +
      makeModelHtml +
      `<div style="color:#444">ASL: ${t.altitude_ft.toLocaleString()} ft</div>` +
      `<div style="color:#444">Spd: ${formatKnots(t.speed_kts)}</div>` +
      `<div style="margin-top:4px;padding-top:3px;border-top:1px solid #eee;display:flex;gap:8px">` +
        `<span style="color:${relCol};font-weight:700" title="Vertical separation from drone">&#8597;&nbsp;${vertStr}</span>` +
        `<span style="color:${relCol};font-weight:700" title="Horizontal distance from drone">&#8596;&nbsp;${horizStr}</span>` +
      `</div>` +
      `<div style="font-size:0.692em;color:${t.trust === 'high' ? '#1d7a40' : '#b45309'};font-weight:600;margin-top:1px">${t.source_services.length} src · ${t.trust === 'high' ? 'High Trust' : 'Low Trust'}</div>` +
      `</div>`
    : '';

  // Hover tooltip — mockup `.ac-tooltip` (L8546-8550 + buildTooltip L8471):
  //   - Always present in DOM; toggled via `display` on mouseenter/mouseleave.
  //   - Starts visible only for unacknowledged alerts (which keep it pinned).
  //   - Content mirrors the mockup row list — Type / ASL / Speed / Hdg / Src /
  //     Trust, plus an alert title + Dist/Time rows when proximity==='alert'.
  //   - Suppressed in surv-ctx alert overlay mode; surv keeps the tooltip
  //     itself but skips the persistent-while-alert + ack-button parts
  //     because mockup `_drawSurvTarget` doesn't surface those.
  const showTooltipPinned = isAlert && !isAcked;
  const typeLabel = rf ? 'RF-detected drone' : t.type === 'heavy' ? 'Commercial Heavy' : t.type === 'medium' ? 'Commercial Jet' : 'GA Aircraft';
  const trustHtml = t.trust === 'high'
    ? '<span style="color:#4ade80">High</span>'
    : t.trust === 'low'
      ? '<span style="color:#fbbf24">Low</span>'
      : '<span style="color:#fbbf24">Medium</span>';
  const srcCount = t.source_services.length;
  const rows: Array<[string, string]> = [
    ['Type', typeLabel],
    ['ASL', `${t.altitude_ft.toLocaleString()} ft`],
    ['Speed', formatKnots(t.speed_kts)],
    ['Hdg', `${t.heading}°`],
    ['Src', `${srcCount}${srcCount === 1 ? ' source' : ' sources'}`],
    ['Trust', trustHtml],
  ];
  if (isAlert) {
    rows.push(['Dist', `<span style="color:#fbbf24">${t.dist_km != null ? `${t.dist_km.toFixed(2)} km` : horizStr}</span>`]);
    rows.push(['Vert', `<span style="color:#fbbf24">${vertStr}</span>`]);
  }
  const rowsHtml = rows.map(([k, v]) =>
    `<div style="display:flex;justify-content:space-between;gap:18px">` +
      `<span style="color:rgba(255,255,255,.5)">${k}</span><span>${v}</span>` +
    `</div>`,
  ).join('');
  const titleHtml = isAlert
    ? `<div style="font-size:0.923em;font-weight:600;color:#fbbf24;margin-bottom:5px;font-family:Inter,sans-serif">⚠ PROXIMITY — ${escapeHtml(rf ? (rf.model ?? rf.serial) : t.callsign)}</div>`
    : `<div style="font-size:0.923em;font-weight:600;color:#fff;margin-bottom:5px;font-family:Inter,sans-serif">${escapeHtml(rf ? (rf.model ?? rf.serial) : t.callsign)}</div>`;
  const tooltipHtml =
    `<div data-role="ac-tooltip" style="display:${showTooltipPinned ? 'block' : 'none'};` +
    `position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%);` +
    `background:#131a11;color:#f0f4f0;border-radius:8px;padding:10px 13px;` +
    `font-family:IBM Plex Mono,monospace;font-size:0.769em;line-height:1.75;white-space:nowrap;` +
    `z-index:600;box-shadow:0 4px 16px rgba(0,0,0,.25);min-width:185px;pointer-events:none">` +
      titleHtml +
      rowsHtml +
      `<div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);` +
      `border:5px solid transparent;border-top-color:#131a11"></div>` +
    `</div>`;

  const ackBtnHtml = (isAlert && !isAcked)
    ? `<div data-role="ack-stack" style="position:absolute;bottom:calc(100% + 8px);left:50%;` +
      `transform:translateX(-50%);z-index:601;pointer-events:auto;width:185px;` +
      `margin-bottom:-12px"><div style="background:#131a11;border-radius:0 0 8px 8px;` +
      `padding:6px 13px 10px;border-top:1px solid rgba(255,255,255,.15)">` +
      `<button data-role="ack-btn" style="width:100%;padding:5px 0;background:#c41a1a;` +
      `color:white;border:none;border-radius:5px;font-size:0.846em;font-weight:700;` +
      `cursor:pointer;font-family:Inter,sans-serif">✓ Acknowledge</button></div></div>`
    : '';

  return { html: planeWrapped + labelHtml + tooltipHtml + ackBtnHtml, boxSize };
}

interface ActiveTarget {
  marker: mapboxgl.Marker;
  el: HTMLElement;
  fingerprint: string;
}

function fingerprint(t: Target, showLabels: boolean, acked: boolean, selRef: string | null, ds: string, sizeStep: number): string {
  return [t.heading, t.proximity, t.trust, t.type, t.callsign, t.registration, t.description, t.rf?.model, t.rf?.serial, t.rf?.freq_mhz, t.rf?.rssi_dbm, t.altitude_ft, t.speed_kts, t.dist_km, t.vert_sep_ft, t.horiz_sep_nm, showLabels, acked, selRef, ds, sizeStep].join('|');
}

export function subscribeTargetMarkers(map: MapboxMap): () => void {
  const active: Record<string, ActiveTarget> = {};
  let rafId: number | null = null;

  const onAck = (id: string) => useTargetsStore.getState().acknowledge(id);
  const onClick = (id: string) => {
    const cur = useTargetsStore.getState().selectedId;
    useTargetsStore.getState().select(cur === id ? null : id);
  };

  const getSelectedDrone = () => {
    const ds = useDronesStore.getState();
    const id = ds.selectedId;
    if (!id) return null;
    const d = ds.byId[id];
    if (!d) return null;
    return { lat: d.telemetry.lat, lng: d.telemetry.lng, agl: d.telemetry.agl };
  };

  const sync = () => {
    const state = useTargetsStore.getState();
    const ui = useUiStore.getState();
    const showLabels = ui.showLabels;

    // Visibility rule lives in lib/map/targetVisibility.ts (shared with the RF
    // operator markers / link layer).
    const visible = visibleTargets();

    const sel = state.selectedId;
    const selectedDrone = getSelectedDrone();
    const dsRef = selectedDrone ? `${selectedDrone.lat}:${selectedDrone.lng}:${selectedDrone.agl}` : '-';
    const seen = new Set<string>();
    const suppressOverlay = ui.activeContext === 'surv';

    for (const t of visible) {
      seen.add(t.target_id);
      const acked = state.ackedIds.has(t.target_id);
      const selected = sel === t.target_id;
      const fp = fingerprint(t, showLabels, acked, sel, dsRef, ui.iconSizeAircraft) + `|${suppressOverlay ? 1 : 0}`;
      const cur = active[t.target_id];

      if (!cur) {
        const el = document.createElement('div');
        const built = buildInnerHtml(t, showLabels, acked, selectedDrone, ui.iconSizeAircraft, selected, suppressOverlay);
        el.style.cssText = `width:${built.boxSize}px;height:${built.boxSize}px;cursor:pointer`;
        el.dataset.tid = t.target_id;
        // Track alert state via dataset so mouseleave can read the live value
        // without holding a stale closure over the original render.
        el.dataset.pinned = (t.proximity === 'alert' && !acked) ? '1' : '0';
        el.innerHTML = built.html;
        const tid = t.target_id;
        el.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          if (target?.closest('[data-role="ack-btn"]')) {
            e.stopPropagation();
            onAck(tid);
            return;
          }
          e.stopPropagation();
          onClick(tid);
        });
        // Sticky hover: tooltip opens on enter and stays open even if the
        // plane drifts out from under the cursor. The document-level
        // mousemove listener (installed in subscribeTargetMarkers below)
        // closes it only after the cursor actually moves.
        el.addEventListener('mouseenter', () => {
          // Close any other open tooltip first (unless it's a pinned alert).
          if (activeHoverEl && activeHoverEl !== el && activeHoverEl.dataset.pinned !== '1') {
            const prev = activeHoverEl.querySelector('[data-role="ac-tooltip"]') as HTMLElement | null;
            if (prev) prev.style.display = 'none';
          }
          const tt = el.querySelector('[data-role="ac-tooltip"]') as HTMLElement | null;
          if (tt) tt.style.display = 'block';
          activeHoverEl = el;
          // Inside the marker → disable dismissal accounting. Movement
          // within the plane bbox must NOT close the tooltip.
          leftZoneX = null;
          leftZoneY = null;
        });
        el.addEventListener('mouseleave', (ev) => {
          if (el !== activeHoverEl) return;
          // Record where the cursor was when it crossed out of the marker.
          // From here the doc-level mousemove watches for displacement.
          // If the cursor stays still (plane just drifted away from it),
          // distance stays 0 and the tooltip persists.
          leftZoneX = ev.clientX;
          leftZoneY = ev.clientY;
        });
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([t.lng, t.lat])
          .addTo(map);
        active[tid] = { marker, el, fingerprint: fp };
        continue;
      }

      if (!mapAnimation.value) cur.marker.setLngLat([t.lng, t.lat]);
      if (cur.fingerprint !== fp) {
        const rebuilt = buildInnerHtml(t, showLabels, acked, selectedDrone, ui.iconSizeAircraft, selected, suppressOverlay);
        cur.el.style.width = `${rebuilt.boxSize}px`;
        cur.el.style.height = `${rebuilt.boxSize}px`;
        cur.el.innerHTML = rebuilt.html;
        cur.el.dataset.pinned = (t.proximity === 'alert' && !acked) ? '1' : '0';
        cur.fingerprint = fp;
        // innerHTML rebuild blows away the previous tooltip element. If this
        // marker is the active hover, the fresh tooltip starts hidden because
        // `showTooltipPinned` is false for non-alerts. Re-show it so a 1 Hz
        // target update doesn't dismiss an open hover tooltip.
        if (cur.el === activeHoverEl) {
          const tt = cur.el.querySelector('[data-role="ac-tooltip"]') as HTMLElement | null;
          if (tt) tt.style.display = 'block';
        }
      }
    }

    for (const id of Object.keys(active)) {
      if (!seen.has(id)) {
        active[id]!.marker.remove();
        delete active[id];
      }
    }
  };

  sync();
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; sync(); });
  };
  const unsubT = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubSel = useTargetsStore.subscribe((s) => s.selectedId, sync);
  const unsubLabels = useUiStore.subscribe((s) => `${s.showLabels}|${s.iconSizeAircraft}`, sync);
  const unsubDrone = useDronesStore.subscribe((s) => s.selectedId, sync);
  const unsubCtx = useUiStore.subscribe((s) => s.activeContext, sync);
  // Fusion state is part of the visibility rule while Live_Fused is selected,
  // so starting/stopping one — or changing its inputs — has to retrigger.
  const unsubSurv = useSurvStore.subscribe(
    (s) => `${s.mode}|${s.selectedNetwork}|${s.fusedService ? 1 : 0}|${s.fusedSelected.join(',')}`,
    sync,
  );
  const unsubAnim = mapAnimation.subscribe((v) => { if (!v) sync(); });

  // Sticky-hover dismissal — close the tooltip only after the cursor has
  // actually moved away from the exit point. Pinned alert markers stay open
  // regardless (mockup `_drawTarget` keeps the proximity overlay on until
  // ack). Installed once per subscribe call; cleanup removes it.
  const onDocMouseMove = (ev: MouseEvent) => {
    if (!activeHoverEl || leftZoneX == null || leftZoneY == null) return;
    if (activeHoverEl.dataset.pinned === '1') return;
    const dx = ev.clientX - leftZoneX;
    const dy = ev.clientY - leftZoneY;
    if (dx * dx + dy * dy <= HOVER_DISMISS_PX * HOVER_DISMISS_PX) return;
    const tt = activeHoverEl.querySelector('[data-role="ac-tooltip"]') as HTMLElement | null;
    if (tt) tt.style.display = 'none';
    activeHoverEl = null;
    leftZoneX = null;
    leftZoneY = null;
  };
  document.addEventListener('mousemove', onDocMouseMove);

  return () => {
    unsubT();
    unsubSel();
    unsubLabels();
    unsubDrone();
    unsubCtx();
    unsubSurv();
    unsubAnim();
    document.removeEventListener('mousemove', onDocMouseMove);
    activeHoverEl = null;
    leftZoneX = null;
    leftZoneY = null;
    for (const id of Object.keys(active)) {
      active[id]!.marker.remove();
      delete active[id];
    }
  };
}
